import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import { useTeamsDataApi } from './useTeamsDataApi'

// Teams list, filterable by org/sport/season/division. Click team → drilldown
// to players (PlayersBrowser handles team_id navigation via shared state).
export default function TeamsBrowser() {
  const api = useTeamsDataApi()
  const [orgs, setOrgs] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [filters, setFilters] = useState({ org_id: '', sport: '', season: '', division: '', search: '' })

  // Bootstrap: fetch orgs once for the filter dropdown.
  useEffect(() => {
    api.listOrgs().then(r => setOrgs(r?.orgs || [])).catch(() => setOrgs([]))
  }, [api])

  const fetchTeams = useCallback(() => {
    setLoading(true); setErr(null)
    api.listTeams(filters)
      .then(r => setTeams(r?.teams || []))
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }, [api, filters])

  useEffect(() => { fetchTeams() }, [fetchTeams])

  const sports = useMemo(() => Array.from(new Set(orgs.map(o => o.sport))).sort(), [orgs])

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filters.org_id} onChange={e => setFilters({ ...filters, org_id: e.target.value })} style={inp}>
          <option value="">All orgs</option>
          {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.short_name || o.org_id}</option>)}
        </select>
        <select value={filters.sport} onChange={e => setFilters({ ...filters, sport: e.target.value })} style={inp}>
          <option value="">All sports</option>
          {sports.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="season (2025-26)" value={filters.season} onChange={e => setFilters({ ...filters, season: e.target.value })} style={{ ...inp, width: 120 }} />
        <input placeholder="division" value={filters.division} onChange={e => setFilters({ ...filters, division: e.target.value })} style={{ ...inp, width: 120 }} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#020617', border: '1px solid #334155', borderRadius: 4, padding: '0 6px' }}>
          <Search size={11} style={{ color: '#888' }} />
          <input placeholder="search name/code" value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
            style={{ ...inp, border: 'none', background: 'transparent', padding: '6px 0' }} />
        </div>
        <span style={{ color: '#888', fontSize: 12 }}>{teams.length} teams</span>
      </div>

      {loading && <Loading />}
      {err && <div style={{ color: '#f87171' }}>{err}</div>}

      {!loading && !err && (
        <div style={{ border: '1px solid #1e293b', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#020617', color: '#94a3b8' }}>
                <th style={th}>Org</th>
                <th style={th}>Sport</th>
                <th style={th}>Code</th>
                <th style={th}>Team</th>
                <th style={th}>Division</th>
                <th style={th}>Season</th>
                <th style={{ ...th, textAlign: 'right' }}>Players</th>
              </tr>
            </thead>
            <tbody>
              {teams.map(t => (
                <tr key={t.team_id} style={{ borderTop: '1px solid #1e293b' }}>
                  <td style={td}>{t.org_id}</td>
                  <td style={td}>{t.sport}</td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#7dd3fc' }}>{t.team_code}</td>
                  <td style={{ ...td, color: '#dbe5f5' }}>{t.display_name}</td>
                  <td style={td}>{t.division || '—'}</td>
                  <td style={td}>{t.season || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{t.player_count}</td>
                </tr>
              ))}
              {teams.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, color: '#888', textAlign: 'center', padding: 16 }}>No teams match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Loading() { return <div style={{ color: '#888', fontSize: 13 }}><Loader2 size={14} className="spin" style={{ display: 'inline', marginRight: 4 }} /> Loading…</div> }
const inp = { padding: '6px 8px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 12 }
const th = { padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600 }
const td = { padding: '6px 10px', color: '#cbd5e1' }
